<script setup>
import { ref, computed } from 'vue'
import { HOST_TULUM, API } from '@/utils/http'
import { AlertCircle } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'

const emit = defineEmits(['estimated'])

const props = defineProps({
  orgId: {
    type: String,
    required: true
  },
  geometry: {
    type: Object,
    required: true
  },
  startTime: {
    type: Date,
    default: null
  },
  endTime: {
    type: Date,
    default: null
  },
  orderType: {
    type: String,
    default: 'IMAGE'
  },
  satElevationAngleMin: {
    type: Number,
    default: 60
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const isLoading = ref(false)
const pricingEstimate = ref(null)
const error = ref(null)

const fetchPricing = async () => {
  if (!props.startTime || !props.endTime) {
    error.value = 'Please select a capture window'
    return
  }

  isLoading.value = true
  error.value = null
  pricingEstimate.value = null

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST_TULUM}/tasking/${props.orgId}/pricing`,
      data: {
        geometry: props.geometry,
        order_type: props.orderType,
        start_time: props.startTime.toISOString(),
        end_time: props.endTime.toISOString(),
        sat_elevation_angle_min: props.satElevationAngleMin
      }
    })

    pricingEstimate.value = response.data
    emit('estimated', pricingEstimate.value)
  } catch (err) {
    console.error('Error fetching pricing:', err)
    error.value = err.response?.data?.detail || 'Failed to get pricing estimate'
  } finally {
    isLoading.value = false
  }
}

const hasEstimate = computed(() => !!pricingEstimate.value)

// Expose for parent components
defineExpose({
  refresh: fetchPricing,
  estimate: pricingEstimate,
  hasEstimate,
  reset: () => {
    pricingEstimate.value = null
    error.value = null
  }
})
</script>

<template>
  <div class="space-y-3">
    <!-- Estimate Button (hidden after estimate is fetched) -->
    <Button
      v-if="!pricingEstimate"
      type="button"
      class="w-full"
      @click="fetchPricing"
      :disabled="disabled || isLoading || !startTime || !endTime"
    >
      <Spinner v-if="isLoading" class="w-4 h-4 mr-2" />
      <span>{{ isLoading ? 'Loading...' : 'Estimate' }}</span>
    </Button>

    <!-- Error Alert -->
    <Alert v-if="error" variant="destructive">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ error }}</AlertDescription>
    </Alert>

    <!-- Estimate Result Banner -->
    <div v-if="pricingEstimate" class="bg-muted p-3 rounded-lg space-y-1">
      <div class="flex justify-between">
        <span class="text-muted-foreground">Estimated Cost:</span>
        <span class="font-semibold">
          {{ pricingEstimate.estimated_cost?.toLocaleString() || 'N/A' }}
          {{ pricingEstimate.units || 'Credits' }}
        </span>
      </div>
      <!-- Only show base price if different from estimated cost (e.g., EXPRESS multiplier applied) -->
      <div v-if="pricingEstimate.pricing_model && pricingEstimate.pricing_model.base_price !== pricingEstimate.estimated_cost" class="flex justify-between text-sm">
        <span class="text-muted-foreground">Base Price:</span>
        <span>{{ pricingEstimate.pricing_model.base_price }} Credits</span>
      </div>
    </div>
  </div>
</template>
